/**
 * Tee in Blue — engraving preview framing (companion to section-tee-in-blue.css)
 *
 * Measures how much letterbox each product's mockup actually has and publishes
 * it as `--mm-tib-preview-crop-auto` on the preview column. The stylesheet
 * turns that into a `transform: scale()` that makes the mockup fill the stage.
 *
 * Why this can't be a constant in the CSS
 * ---------------------------------------
 * TIB drops each product's mockup photo into a square stage. The photos are
 * not one shape: across the 293 TIB-enabled products in this catalogue the
 * mockup aspect ratio runs from 1.31:1 to 1.63:1, and 148 of them are exactly
 * square with no letterbox at all. A single hard-coded crop would leave a band
 * on the wide ones and — far worse — crop into the product itself on every
 * square one. Nothing in TIB's DOM exposes the mockup's aspect ratio to a CSS
 * selector (its mockup <img> carries width="300" height="300" whatever the
 * real photo's shape), so the amount has to be measured.
 *
 * The measurement is layout-only (`offsetHeight`), which a CSS transform does
 * not affect, so applying the result can never feed back into the next
 * measurement.
 *
 * This is progressive enhancement. If the script never runs, the custom
 * property stays unset, the stylesheet falls back to a crop of 0, and the
 * preview keeps TIB's stock framing.
 */
(function () {
	'use strict'

	var WRAPPER_SELECTOR = '.product__main'
	var AUTO_CROP_PROPERTY = '--mm-tib-preview-crop-auto'

	// Below this much letterbox there is nothing worth reclaiming, and treating
	// it as zero keeps sub-pixel rounding from producing a 1.0001 scale.
	var MIN_FILL = 1.001

	// Above this, we are not looking at a letterbox — we are looking at a frame
	// mid-render, where TIB has sized the stage but not yet the mockup. The
	// widest mockup in the catalogue is 1.63:1, so anything past 2.5 is noise,
	// and acting on it would blow the preview up by hundreds of times for a
	// frame. Reject the reading and keep the last good one.
	var MAX_FILL = 2.5

	/**
	 * Fraction of the mockup's width that has to be cropped for the mockup to
	 * fill the stage's height. Returns 0 when the mockup already fills it (a
	 * square mockup has nothing to reclaim), and null when the DOM is not in a
	 * state worth measuring.
	 */
	function measureCrop(wrapper) {
		// The zoom layer holds a second copy of the mockup at a fixed 1000px.
		// Always measure the on-screen one.
		var item =
			wrapper.querySelector('.tee-hover-zoom-normal .tee-mockup-item') ||
			wrapper.querySelector('.tee-mockup-item')
		if (!item) return null

		var mockup = item.querySelector('.tee-mockup')
		if (!mockup) return null

		var stageHeight = item.offsetHeight
		var mockupHeight = mockup.offsetHeight
		if (!stageHeight || !mockupHeight) return null

		var fill = stageHeight / mockupHeight
		if (fill > MAX_FILL) return null
		if (fill <= MIN_FILL) return 0

		return 1 - 1 / fill
	}

	function publishMeasuredCrop(wrapper) {
		var crop = measureCrop(wrapper)
		if (crop === null) return

		var next = crop.toFixed(4)
		// Only write on change, so the MutationObserver below can't chase its
		// own tail.
		if (wrapper.style.getPropertyValue(AUTO_CROP_PROPERTY) !== next) {
			wrapper.style.setProperty(AUTO_CROP_PROPERTY, next)
		}
	}

	function start() {
		var wrapper = document.querySelector(WRAPPER_SELECTOR)
		if (!wrapper) return

		var pending = null
		function schedule() {
			if (pending) return
			pending = requestAnimationFrame(function () {
				pending = null
				publishMeasuredCrop(wrapper)
			})
		}

		schedule()

		// TIB injects its gallery into this column, swaps mockups when the
		// shopper changes template or variant, and rewrites the inline pixel
		// geometry as it goes. Watch for all of it rather than guessing at a
		// settle time. Observing the preview column rather than the document
		// keeps the callback off every unrelated mutation on the page — on a
		// product where TIB never loads, nothing in here ever fires.
		new MutationObserver(schedule).observe(wrapper, {
			subtree: true,
			childList: true,
			attributes: true,
			attributeFilter: ['style', 'class'],
		})

		// The mockup's box is only correct once its photo has decoded.
		window.addEventListener('load', schedule)
		window.addEventListener('resize', schedule)
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', start)
	} else {
		start()
	}
})()
